import OBSWebSocket, { EventSubscription, OBSEventTypes, OBSResponseTypes, OBSWebSocketError } from 'obs-websocket-js';
import { ObservableField } from 'shared/utils/observer-field';
import { OPERATOR_MARKER_OBS_SCENE_NAME, OPERATOR_MARKER_SOURCE_NAME } from 'modules/video-markers/domain/constants';
import {
  NativeMarkerResult,
  RecorderClock,
  RecorderConfig,
  RecorderConnection,
  RecorderDisconnectReason,
  RecorderPosition,
} from 'modules/video-markers/domain/interfaces';

/** Коды закрытия сокета obs-websocket 5 (`WebSocketCloseCode`) и браузера. */
const CLOSE_CODE_AUTHENTICATION_FAILED = 4009;
const CLOSE_CODE_ABNORMAL = 1006;

/**
 * Коды ответа `CreateRecordChapter`, при которых глава не ставится не по ошибке, а потому что
 * программа не может: запись не идёт (`OutputNotRunning`), формат записи не Hybrid MP4
 * (`RequestProcessingFailed`), obs-websocket старше 5.5 (`UnknownRequestType`).
 */
const CHAPTER_UNSUPPORTED_CODES = new Set([204, 501, 702]);

/** Коды ответа obs-websocket 5 (`RequestStatus`): сцены, источника или элемента нет / уже есть. */
const RESOURCE_NOT_FOUND = 600;
const RESOURCE_ALREADY_EXISTS = 601;

/** Служебный источник хоткея: «Цвет» 2×2, полностью прозрачный (ABGR `0`). */
const OPERATOR_MARKER_INPUT_KIND = 'color_source_v3';
const OPERATOR_MARKER_INPUT_SETTINGS = { color: 0, width: 2, height: 2 };

const RECONNECT_BASE_DELAY_MS = 1000;
const RECONNECT_MAX_DELAY_MS = 30_000;

/** Состояния `RecordStateChanged.outputState` (`ObsOutputState` obs-websocket 5). */
const RECORD_STARTED = 'OBS_WEBSOCKET_OUTPUT_STARTED';
const RECORD_ENDED = new Set(['OBS_WEBSOCKET_OUTPUT_STOPPING', 'OBS_WEBSOCKET_OUTPUT_STOPPED']);

/** Позиция OBS — миллисекунды (`outputDuration`). */
const OBS_PRECISION_MS = 1;

/** То, чем адаптер пользуется у `obs-websocket-js`; отдельный тип — чтобы подменять в тестах. */
export type ObsWebSocketClient = Pick<OBSWebSocket, 'connect' | 'disconnect' | 'call' | 'callBatch' | 'on'>;

function closeCodeToReason(code: number | undefined): RecorderDisconnectReason {
  if (code === CLOSE_CODE_AUTHENTICATION_FAILED) return 'auth';
  if (code === CLOSE_CODE_ABNORMAL) return 'unreachable';
  return 'unknown';
}

function errorCode(error: unknown): number | undefined {
  return (error as Partial<OBSWebSocketError> | undefined)?.code;
}

/** Для `.catch`: проглотить ошибку с этим кодом, остальные пробросить. */
const ignoreErrorCode = (code: number) => (error: unknown) => {
  if (errorCode(error) !== code) throw error;
};

/**
 * Адаптер порта `RecorderClock` для OBS Studio через obs-websocket 5 (OBS 28+). Оверлей открыт
 * Browser Source в том же OBS, поэтому сервер по умолчанию — `127.0.0.1:4455`.
 *
 * После обрыва переподключается с удвоением паузы (1 с → 30 с). Неверный пароль — не обрыв:
 * повторять бессмысленно, адаптер остаётся в `disconnected / auth`, пока ссылку не поправят.
 *
 * Хоткей оператора: после подключения адаптер заводит (если нет) сцену `OPERATOR_MARKER_OBS_SCENE_NAME`
 * с выключенным прозрачным источником `OPERATOR_MARKER_SOURCE_NAME`. Оператор вешает клавишу на
 * «Показать» этого элемента; включение — одна метка, и адаптер тут же выключает элемент обратно.
 * Сцена в эфир не выходит, а хоткей OBS глобальный — срабатывает и при свёрнутом OBS.
 *
 * Путь файла записи `GetRecordStatus` не отдаёт. OBS 32 называет его в `RecordStateChanged` со
 * статусом `OBS_WEBSOCKET_OUTPUT_STARTED` (типы `obs-websocket-js` обещают путь только при
 * остановке — проверено на OBS 32.2.1, это не так) и в `RecordFileChanged` при разбиении записи.
 * Адаптер запоминает путь из этих событий; подключившись посреди записи, он пути не знает.
 */
export class ObsRecorderClock implements RecorderClock {
  readonly kind = 'obs' as const;
  readonly connection = new ObservableField<RecorderConnection>({ status: 'idle' });

  private isStopped = true;
  private reconnectAttempt = 0;
  private reconnectTimer: ReturnType<typeof setTimeout> | null = null;
  private readonly operatorMarkerListeners = new Set<() => void>();
  /** Элемент служебного источника в служебной сцене; `null` — не заведён, хоткей не ловим. */
  private operatorMarkerItemId: number | null = null;
  /** Файл идущей записи из событий OBS; `null` — запись не идёт или старт был до подключения. */
  private recordingFile: string | null = null;

  constructor(
    private readonly config: RecorderConfig,
    private readonly client: ObsWebSocketClient = new OBSWebSocket(),
  ) {
    this.client.on('ConnectionClosed', this.handleConnectionClosed);
    this.client.on('SceneItemEnableStateChanged', this.handleSceneItemEnableStateChanged);
    this.client.on('RecordStateChanged', this.handleRecordStateChanged);
    this.client.on('RecordFileChanged', this.handleRecordFileChanged);
  }

  async connect(): Promise<void> {
    this.isStopped = false;
    this.clearReconnectTimer();
    await this.tryConnect();
  }

  disconnect(): void {
    this.isStopped = true;
    this.clearReconnectTimer();
    this.connection.value = { status: 'idle' };
    void this.client.disconnect();
  }

  async getPosition(): Promise<RecorderPosition> {
    const [record, stream] = await this.client.callBatch([{ requestType: 'GetRecordStatus' }, { requestType: 'GetStreamStatus' }]);

    if (!record?.requestStatus.result || !stream?.requestStatus.result) {
      throw new Error('OBS не вернул состояние записи и эфира');
    }

    const recordStatus = record.responseData as OBSResponseTypes['GetRecordStatus'];
    const streamStatus = stream.responseData as OBSResponseTypes['GetStreamStatus'];

    return {
      recordingMs: recordStatus.outputActive ? recordStatus.outputDuration : null,
      recordingPaused: recordStatus.outputActive && recordStatus.outputPaused,
      recordingFile: recordStatus.outputActive ? this.recordingFile : null,
      streamingMs: streamStatus.outputActive ? streamStatus.outputDuration : null,
      precisionMs: OBS_PRECISION_MS,
    };
  }

  async writeNativeMarker(label: string): Promise<NativeMarkerResult> {
    try {
      await this.client.call('CreateRecordChapter', { chapterName: label });
      return 'written';
    } catch (error) {
      const code = errorCode(error);
      return code !== undefined && CHAPTER_UNSUPPORTED_CODES.has(code) ? 'unsupported' : 'failed';
    }
  }

  onOperatorMarker(listener: () => void): () => void {
    this.operatorMarkerListeners.add(listener);
    return () => {
      this.operatorMarkerListeners.delete(listener);
    };
  }

  private async tryConnect(): Promise<void> {
    this.connection.value = { status: 'connecting' };
    // Пока связи не было, запись могли перезапустить — прежний путь больше не верен.
    this.recordingFile = null;
    const { host, port, password } = this.config;

    try {
      await this.client.connect(`ws://${host}:${port}`, password, {
        rpcVersion: 1,
        // Позиция запрашивается по требованию; из событий нужны хоткей оператора и путь файла записи.
        eventSubscriptions: EventSubscription.SceneItems | EventSubscription.Outputs,
      });
    } catch (error) {
      if (this.isStopped) return;
      const reason = closeCodeToReason(errorCode(error));
      this.connection.value = { status: 'disconnected', reason };
      if (reason !== 'auth') this.scheduleReconnect();
      return;
    }

    if (this.isStopped) {
      void this.client.disconnect();
      return;
    }
    this.reconnectAttempt = 0;
    this.connection.value = { status: 'connected' };

    try {
      await this.ensureOperatorMarkerItem();
    } catch {
      // Без служебного источника не работает только хоткей; позиция и метки событий — работают.
      this.operatorMarkerItemId = null;
    }
  }

  /**
   * Сцена и выключенный элемент служебного источника. Включённый элемент сбрасывается молча: его
   * включили, пока адаптера не было, и когда — неизвестно.
   */
  private async ensureOperatorMarkerItem(): Promise<void> {
    const sceneName = OPERATOR_MARKER_OBS_SCENE_NAME;
    const sourceName = OPERATOR_MARKER_SOURCE_NAME;
    let sceneItemId: number;

    try {
      ({ sceneItemId } = await this.client.call('GetSceneItemId', { sceneName, sourceName }));
    } catch (error) {
      if (errorCode(error) !== RESOURCE_NOT_FOUND) throw error;
      await this.client.call('CreateScene', { sceneName }).catch(ignoreErrorCode(RESOURCE_ALREADY_EXISTS));
      sceneItemId = await this.createOperatorMarkerItem();
    }

    this.operatorMarkerItemId = sceneItemId;
    await this.client.call('SetSceneItemEnabled', { sceneName, sceneItemId, sceneItemEnabled: false });
  }

  private async createOperatorMarkerItem(): Promise<number> {
    const sceneName = OPERATOR_MARKER_OBS_SCENE_NAME;
    const sourceName = OPERATOR_MARKER_SOURCE_NAME;

    try {
      const { sceneItemId } = await this.client.call('CreateInput', {
        sceneName,
        inputName: sourceName,
        inputKind: OPERATOR_MARKER_INPUT_KIND,
        inputSettings: OPERATOR_MARKER_INPUT_SETTINGS,
        sceneItemEnabled: false,
      });
      return sceneItemId;
    } catch (error) {
      // Источник с этим именем уже есть (его убрали из служебной сцены) — вернуть его туда элементом.
      if (errorCode(error) !== RESOURCE_ALREADY_EXISTS) throw error;
      const { sceneItemId } = await this.client.call('CreateSceneItem', { sceneName, sourceName, sceneItemEnabled: false });
      return sceneItemId;
    }
  }

  /** Включение служебного элемента — нажатие хоткея. Своё выключение приходит тем же событием и пропускается. */
  private handleSceneItemEnableStateChanged = (event: OBSEventTypes['SceneItemEnableStateChanged']) => {
    const { sceneName, sceneItemId, sceneItemEnabled } = event;
    if (!sceneItemEnabled || sceneName !== OPERATOR_MARKER_OBS_SCENE_NAME || sceneItemId !== this.operatorMarkerItemId) {
      return;
    }

    this.operatorMarkerListeners.forEach((listener) => listener());
    // Не сбросился — следующее нажатие события не даст; сбросит переподключение (`ensureOperatorMarkerItem`).
    this.client.call('SetSceneItemEnabled', { sceneName, sceneItemId, sceneItemEnabled: false }).catch(() => undefined);
  };

  /**
   * Путь приходит при `STARTED` (и ещё раз при `STOPPED`). Остановка его сбрасывает, а пауза
   * (`PAUSED` / `RESUMED`) — нет: запись продолжается в тот же файл.
   */
  private handleRecordStateChanged = (event: OBSEventTypes['RecordStateChanged']) => {
    if (event.outputState === RECORD_STARTED) this.recordingFile = event.outputPath || null;
    else if (RECORD_ENDED.has(event.outputState)) this.recordingFile = null;
  };

  /** Разбиение записи на файлы (настройка «Автоматически разделять файл») — запись идёт в новый файл. */
  private handleRecordFileChanged = (event: OBSEventTypes['RecordFileChanged']) => {
    this.recordingFile = event.newOutputPath || null;
  };

  /** Обрыв уже установленного подключения. Неудачный `connect` обрабатывает `tryConnect`. */
  private handleConnectionClosed = (error: OBSWebSocketError) => {
    if (this.isStopped || this.connection.value.status !== 'connected') return;
    const reason = closeCodeToReason(error?.code);
    this.connection.value = { status: 'disconnected', reason };
    if (reason !== 'auth') this.scheduleReconnect();
  };

  private scheduleReconnect() {
    this.clearReconnectTimer();
    const delay = Math.min(RECONNECT_BASE_DELAY_MS * 2 ** this.reconnectAttempt, RECONNECT_MAX_DELAY_MS);
    this.reconnectAttempt += 1;
    this.reconnectTimer = setTimeout(() => {
      this.reconnectTimer = null;
      void this.tryConnect();
    }, delay);
  }

  private clearReconnectTimer() {
    if (this.reconnectTimer !== null) {
      clearTimeout(this.reconnectTimer);
      this.reconnectTimer = null;
    }
  }
}
